import type { IdFactory, ItineraryEditor, ItinerarySegment, RailwayInsertionDraft } from "../types";
import { insertNodeAfter, insertNodeBefore, rebuildAdjacentSegments, resolveDayEndpointNodes } from "./itineraryEditorService";
import { applyTransportWorkspaceChange, captureTransportWorkspaceBaseline } from "./itineraryAdvancedService";
import { deferInsertedRailwayToNextDay, railwayOvernightDays, shiftRailwayPlanDates, splitInsertedOvernightRailway } from "./itineraryRailwayOvernightService";
import { refreshInsertedRailwayTransfer } from "./itineraryRailwayTransferService";
import { rebuildProfessionalDay } from "./itineraryProfessionalService";

export type { RailwayInsertionDraft } from "../types";

/** 将完整火车方案一次插入点位前后；add/empty 的目标为行程日 ID。 */
export function insertSelectedRailway(
  editor: ItineraryEditor,
  targetNodeId: string,
  placement: "before" | "after" | "empty" | "add",
  draft: RailwayInsertionDraft,
  idFactory: IdFactory,
): ItineraryEditor {
  if (placement === "add") {
    const day = editor.days.find((item) => item.id === targetNodeId);
    if (!day) throw new Error("找不到火车插入位置，请重新打开后重试");
    if (!day.nodes.length) return insertSelectedRailway(editor, day.id, "empty", draft, idFactory);
    const slots = ["morning", "afternoon", "evening"];
    const majorId = draft.majorId ?? editor.professionSettings?.mainMajorId;
    const track = day.parallelTracks?.tracks.find((item) => item.majorId === majorId)
      ?? day.parallelTracks?.tracks.find((item) => item.majorId === editor.professionSettings?.mainMajorId);
    const ordered = track
      ? track.nodeIds.map((id) => day.nodes.find((node) => node.id === id)!)
      : resolveDayEndpointNodes(day);
    if (!ordered.length || ordered.some((node) => !node)) throw new Error("找不到火车所属专业的插入位置");
    const later = ordered.find((node) => slots.indexOf(node.timeSlot) > slots.indexOf(draft.timeSlot));
    const last = ordered.at(-1)!;
    const terminal = ordered.length > 1 && last.timeSlot === draft.timeSlot
      && ["stay_area", "organization_site"].includes(last.nodeType) ? last : null;
    const before = later ?? terminal;
    return insertSelectedRailway(editor, (before ?? last).id, before ? "before" : "after", draft, idFactory);
  }
  const firstSelection = draft.railwayTransfer?.legs[0] ?? draft.segment.sourcePayload?.selected;
  const lastSelection = draft.railwayTransfer?.legs.at(-1) ?? firstSelection;
  if (!firstSelection || !lastSelection) throw new Error("请选择火车方案");
  if (draft.origin.nodeType !== "railway_station" || draft.destination.nodeType !== "railway_station"
    || !draft.origin.pointSpuId || !draft.destination.pointSpuId
    || draft.origin.pointSpuId === draft.destination.pointSpuId
    || draft.origin.pointSpuId !== firstSelection.fromStationId
    || draft.destination.pointSpuId !== lastSelection.toStationId) {
    throw new Error("火车方案的实际车站无法确认，请重新查询");
  }
  const insert = placement === "before" ? insertNodeBefore : insertNodeAfter;
  const targetDay = editor.days.find((day) => placement === "empty" ? day.id === targetNodeId : day.nodes.some((node) => node.id === targetNodeId));
  const target = targetDay?.nodes.find((node) => node.id === targetNodeId);
  if (!targetDay || (placement !== "empty" && !target)) throw new Error("找不到火车插入位置，请重新打开后重试");
  if (placement === "empty" && targetDay.nodes.length) throw new Error("当天已不是空白，请重新选择火车插入位置");
  // 先保持两站相邻，应用真实车次时再归位；不能逐站移动而拆开既有站对。
  const origin = { ...draft.origin, timeSlot: target?.timeSlot ?? draft.timeSlot, railwayRegionName: null };
  const destination = { ...draft.destination, timeSlot: target?.timeSlot ?? draft.timeSlot, railwayRegionName: null };
  const existingIds = new Set(editor.days.flatMap((day) => day.nodes.map((node) => node.id)));
  let next: ItineraryEditor;
  let fromNodeId: string;
  let toNodeId: string;
  if (placement !== "empty" && (targetDay.parallelTracks?.tracks.length || (draft.majorId && draft.majorId !== editor.professionSettings?.mainMajorId))) {
    const first = insert(editor, targetNodeId, placement === "before" ? destination : origin, idFactory, { majorId: draft.majorId });
    const firstId = first.days.flatMap((day) => day.nodes).find((node) => !existingIds.has(node.id))?.id;
    if (!firstId) throw new Error("火车节点插入失败");
    existingIds.add(firstId);
    next = insert(first, firstId, placement === "before" ? origin : destination, idFactory, { majorId: draft.majorId });
    const secondId = next.days.flatMap((day) => day.nodes).find((node) => !existingIds.has(node.id))?.id;
    if (!secondId) throw new Error("火车节点插入失败");
    fromNodeId = placement === "before" ? secondId : firstId;
    toNodeId = placement === "before" ? firstId : secondId;
  } else {
    next = structuredClone(editor);
    const day = next.days.find((item) => item.id === targetDay.id)!;
    fromNodeId = idFactory();
    toNodeId = idFactory();
    const index = placement === "empty" ? 0 : day.nodes.findIndex((node) => node.id === targetNodeId) + (placement === "after" ? 1 : 0);
    day.nodes.splice(index, 0, { ...origin, id: fromNodeId, nodeType: "railway_station", sortOrder: 1 }, { ...destination, id: toNodeId, nodeType: "railway_station", sortOrder: 1 });
    if (placement === "empty" && next.professionSettings && draft.majorId) {
      const major = next.professionSettings.majors.find((item) => item.id === draft.majorId);
      if (!major) throw new Error("所选专业不在行程设置中");
      day.parallelTracks = { version: 1, tracks: [{ id: idFactory(), majorId: major.id, title: major.name,
        nodeIds: [fromNodeId, toNodeId], segmentIds: [] }] };
      next = rebuildProfessionalDay(next, day.id, idFactory);
    }
    for (const slot of ["morning", "afternoon", "evening"]) day.nodes.filter((node) => node.timeSlot === slot).forEach((node, order) => { node.sortOrder = order + 1; });
    next.segments = rebuildAdjacentSegments(next.days, next.segments, idFactory, { preserveStationPairs: false });
  }
  // 公共首尾可能生成多个专业区间，只向本次选择的专业写入方案。
  let rail = next.segments.find((segment) => segment.fromNodeId === fromNodeId && segment.toNodeId === toNodeId
    && (!segment.majorId || segment.majorId === (draft.majorId ?? next.professionSettings?.mainMajorId)));
  if (!rail) throw new Error("无法确定火车所属专业路线");
  if (needsNextDayDeparture(next, rail, firstSelection.departureAt)) {
    next = deferInsertedRailwayToNextDay(next, rail.id, idFactory);
    next.segments = rebuildAdjacentSegments(next.days, next.segments, idFactory);
    // 当晚原住宿没有换位置，恢复插入前已填写的到店交通事实。
    const restoredIds = new Map<string, string>();
    next.segments = next.segments.map((segment) => {
      const previous = editor.segments.find((item) => item.fromNodeId === segment.fromNodeId && item.toNodeId === segment.toNodeId
        && item.startDayId === segment.startDayId && item.endDayId === segment.endDayId && (item.majorId ?? null) === (segment.majorId ?? null));
      if (!previous) return segment;
      restoredIds.set(segment.id, previous.id);
      return { ...structuredClone(previous), routeSeq: segment.routeSeq };
    });
    for (const day of next.days) for (const track of day.parallelTracks?.tracks ?? []) {
      track.segmentIds = track.segmentIds.map((id) => restoredIds.get(id) ?? id);
    }
    const railId = rail.id;
    rail = next.segments.find((segment) => segment.id === railId)!;
    draft = structuredClone(draft);
    shiftRailwayPlanDates(draft.segment, 1);
    if (draft.railwayTransfer) {
      for (const plan of [draft.railwayTransfer, ...draft.railwayTransfer.backups ?? []]) {
        // 只顺延计划腿；方案快照仍是实际查询日返回的证据。
        const shifted = { ...draft.segment, sourcePayload: { version: 1 as const, backups: plan.legs } };
        shiftRailwayPlanDates(shifted, 1);
        plan.legs = shifted.sourcePayload.backups;
      }
    }
  }
  next = applyTransportWorkspaceChange(next, {
    ...captureTransportWorkspaceBaseline(next, rail),
    segment: {
      ...draft.segment, id: rail.id, fromNodeId, toNodeId, startDayId: rail.startDayId, endDayId: rail.endDayId,
      routeSeq: rail.routeSeq, majorId: rail.majorId, routeType: "main_transport", transportMode: "rail",
      sourcePayload: { version: 1, backups: [], ...draft.segment.sourcePayload, railwayInsertionPlacement: placement === "empty" ? "after" : placement },
    },
    endpointChanges: [], railwayTransfer: draft.railwayTransfer,
  });
  const insertedRails = next.segments.filter((segment) => segment.id === rail.id
    || segment.sourcePayload?.railwayTransfer?.segmentIds.includes(rail.id));
  for (const inserted of insertedRails) {
    if (inserted.transportMode === "rail" && railwayOvernightDays(inserted.departureAt, inserted.arrivalAt)) {
      next = splitInsertedOvernightRailway(next, inserted.id, draft.trainStayAreaId ?? "", idFactory, new Set(insertedRails.map((segment) => segment.id)));
    }
  }
  next.segments = rebuildAdjacentSegments(next.days, next.segments, idFactory);
  return refreshInsertedRailwayTransfer(next, insertedRails.map((segment) => segment.id));
}

/** 沿插入前的真实路线比较钟点，参考查询日期不参与业务合法性判断。 */
function needsNextDayDeparture(editor: ItineraryEditor, rail: ItinerarySegment, departure?: string | null): boolean {
  if (!departure) return false;
  const departureTime = departure.slice(11, 19);
  const day = editor.days.find((item) => item.id === rail.startDayId)!;
  const incoming = new Map(editor.segments.filter((segment) => segment.endDayId === day.id
    && (segment.majorId ?? null) === (rail.majorId ?? null)).map((segment) => [segment.toNodeId, segment]));
  let previous = incoming.get(rail.fromNodeId);
  const seen = new Set<string>();
  while (previous && !seen.has(previous.fromNodeId)) {
    seen.add(previous.fromNodeId);
    const node = day.nodes.find((item) => item.id === previous!.fromNodeId);
    if (!node) break;
    const arrival = incoming.get(node.id)?.arrivalAt;
    const times = [node.planStartAt, node.planEndAt, arrival].filter((value): value is string => Boolean(value)).map((value) => value.slice(11, 19));
    const earliest = { morning: "00:00:00", afternoon: "12:00:00", evening: "18:00:00" }[node.timeSlot];
    if (departureTime < [earliest, ...times].sort().at(-1)!) return true;
    previous = incoming.get(node.id);
  }
  return false;
}
